import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_CONFIGS } from '../data/designVariants';
import { CONFIG_STORAGE_KEY, REVALIDATE_MS, history as readHistory, load, publish as persist, type ConfigMap, type ConfigSource } from '../services/designVariantStore';
import type { DesignVariantChange, DesignVariantConfig, SurfaceKey } from '../types/experience';

/**
 * Holds the published design configuration for every surface.
 *
 * One source of truth for the whole application: the homepage controller, the
 * housing controller, Discover and the admin console all read the same record.
 * A published change reaches an open session without a reload, without
 * navigating anyone anywhere and without ending their session:
 *
 *   • on load, at application bootstrap;
 *   • on a timer, so a session left open picks up a later publish;
 *   • when the tab is brought back to the foreground, because a backgrounded
 *     tab has been running against a configuration that may have moved on;
 *   • immediately in sibling tabs of the same browser.
 */
interface DesignVariantValue {
  configs: ConfigMap;
  /** False until the configuration service has answered. */
  ready: boolean;
  /** Where the configuration in use came from — server, this browser, or defaults. */
  source: ConfigSource;
  /** Set when configuration could not be loaded; the last known record is in use. */
  loadError: string | null;
  /** When the configuration was last read successfully. */
  lastReadAt: string | null;
  changes: DesignVariantChange[];
  configFor: (surface: SurfaceKey) => DesignVariantConfig;
  publish: (next: DesignVariantConfig, reason: string, by: string) => Promise<void>;
  /** Re-read the published configuration now. */
  refresh: () => Promise<void>;
}
const Ctx = createContext<DesignVariantValue | null>(null);
export function DesignVariantProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [configs, setConfigs] = useState<ConfigMap>(DEFAULT_CONFIGS);
  const [ready, setReady] = useState(false);
  const [source, setSource] = useState<ConfigSource>('defaults');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);
  const [changes, setChanges] = useState<DesignVariantChange[]>([]);
  const mounted = useRef(true);
  const refresh = useCallback(async () => {
    try {
      const result = await load();
      if (!mounted.current) return;
      setConfigs(result.configs);
      setSource(result.source);
      setLoadError(result.error);
      if (!result.error) setLastReadAt(new Date().toISOString());
    } catch {
      if (!mounted.current) return;
      // The last known configuration stays in place. A surface presents its
      // fallback design; nothing goes blank.
      setLoadError('Design configuration could not be read. The last known configuration is in use.');
    } finally {
      if (mounted.current) setReady(true);
    }
  }, []);
  useEffect(() => {
    mounted.current = true;
    refresh();
    readHistory().then(past => {
      if (mounted.current) setChanges(past);
    });
    return () => {
      mounted.current = false;
    };
  }, [refresh]);

  // Controlled revalidation. Not on render, and not on every navigation — a
  // published change reaches an open session within one interval.
  useEffect(() => {
    const timer = window.setInterval(refresh, REVALIDATE_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', refresh);
    };
  }, [refresh]);

  // A publish in one tab reaches the others at once. Two open tabs disagreeing
  // about what is live would be impossible for anyone to explain.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== CONFIG_STORAGE_KEY) return;
      refresh();
      readHistory().then(past => {
        if (mounted.current) setChanges(past);
      });
    };
    window.addEventListener('storage', onStorage);
    let channel: BroadcastChannel | null = null;
    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel('campuzon.design.config');
      channel.onmessage = () => refresh();
    }
    return () => {
      window.removeEventListener('storage', onStorage);
      channel?.close();
    };
  }, [refresh]);
  const publish = useCallback(async (next: DesignVariantConfig, reason: string, by: string) => {
    const before = configs[next.surface];
    const stamped: DesignVariantConfig = {
      ...next,
      updatedAt: new Date().toISOString(),
      updatedBy: by
    };
    const change: DesignVariantChange = {
      id: `dvc-${Date.now()}`,
      surface: next.surface,
      at: stamped.updatedAt,
      by,
      reason,
      before,
      after: stamped
    };
    const merged = await persist(stamped, change);
    setConfigs(merged);
    setChanges(current => [change, ...current].slice(0, 50));
    setLastReadAt(new Date().toISOString());
    setLoadError(null);
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel('campuzon.design.config');
      channel.postMessage({
        published: next.surface,
        at: stamped.updatedAt
      });
      channel.close();
    }
  }, [configs]);
  const value = useMemo<DesignVariantValue>(() => ({
    configs,
    ready,
    source,
    loadError,
    lastReadAt,
    changes,
    configFor: surface => configs[surface] ?? DEFAULT_CONFIGS[surface],
    publish,
    refresh
  }), [configs, ready, source, loadError, lastReadAt, changes, publish, refresh]);
  return <Ctx.Provider value={value} data-id="element-6055" data-mp="e1yujvjwcl39">{children}</Ctx.Provider>;
}
export function useDesignVariants(): DesignVariantValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useDesignVariants must be used inside DesignVariantProvider');
  return value;
}